import React, { createContext, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { usePublicPage } from "../../../../hooks/usePublicPage";
import { resolveMediaUrl } from "../../../../utils/mediaUrl";
import { getApiErrorMessage } from "../../../../utils/apiResponse";
import { useLanguage } from "../../../../context/LanguageContext";
import "./Hero.css";

const HeroContext = createContext(null);

const requireField = (fields, key) => {
  const value = fields?.[key];
  if (value == null || String(value).trim() === "") {
    throw new Error(`Hero field "${key}" is missing from the API response.`);
  }
  return value;
};

const buildHeroModel = (section) => {
  const f = section.fields || {};
  const banner =
    section.media?.groups?.banner?.[0] ||
    section.media?.primary ||
    null;

  if (!banner?.url) {
    throw new Error("Hero banner media is missing from the API response.");
  }

  return {
    badge: requireField(f, "badge"),
    title: requireField(f, "title"),
    subtitle: requireField(f, "subtitle"),
    howItWorksButtonText: requireField(f, "howItWorksButtonText"),
    howItWorksButtonUrl: requireField(f, "howItWorksButtonUrl"),
    reportButtonText: requireField(f, "reportButtonText"),
    reportButtonUrl: requireField(f, "reportButtonUrl"),
    bannerAlt: requireField(f, "bannerAlt"),
    bannerUrl: resolveMediaUrl(banner.url),
    stats: [
      {
        value: requireField(f, "stat1Value"),
        label: requireField(f, "stat1Label"),
      },
      {
        value: requireField(f, "stat2Value"),
        label: requireField(f, "stat2Label"),
      },
      {
        value: requireField(f, "stat3Value"),
        label: requireField(f, "stat3Label"),
      },
    ],
  };
};

const isExternal = (href) => /^https?:\/\//i.test(href);

const HeroAction = ({ href, className, children }) => {
  if (isExternal(href)) {
    return (
      <a href={href} className={className} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }

  if (href.startsWith("#") || href.includes("/#")) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
};

const TitleLines = ({ title }) => {
  const lines = String(title).split("\n").filter((line) => line.length > 0);
  return (
    <>
      {lines.map((line, index) => (
        <React.Fragment key={`${index}-${line}`}>
          {index > 0 ? <br /> : null}
          {line}
        </React.Fragment>
      ))}
    </>
  );
};

export const HeroProvider = ({ children }) => {
  const { meta } = useLanguage();
  const pageQuery = usePublicPage("home");

  const { model, modelError } = useMemo(() => {
    if (!pageQuery.data) {
      return { model: null, modelError: null };
    }

    try {
      const hero = pageQuery.data.sections?.find((s) => s.key === "hero");
      if (!hero) {
        throw new Error("Hero section was not found on the home page.");
      }
      return { model: buildHeroModel(hero), modelError: null };
    } catch (err) {
      return {
        model: null,
        modelError: err instanceof Error ? err.message : String(err),
      };
    }
  }, [pageQuery.data]);

  const error =
    pageQuery.error != null ? getApiErrorMessage(pageQuery.error) : modelError;
  const loading = pageQuery.isPending && !model;

  return (
    <HeroContext.Provider value={{ loading, error, model, dir: meta.dir }}>
      {children}
    </HeroContext.Provider>
  );
};

const useHero = () => {
  const ctx = useContext(HeroContext);
  if (!ctx) {
    throw new Error("Hero components must be used within HeroProvider.");
  }
  return ctx;
};

export const HeroContent = () => {
  const { loading, error, model, dir } = useHero();

  if (loading && !model) {
    return (
      <div className="hero__content" dir={dir}>
        <p style={{ color: "#64748b", fontSize: 14 }}>Loading hero…</p>
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="hero__content" dir={dir}>
        <p style={{ color: "#b91c1c", fontSize: 14 }}>{error || "Hero unavailable."}</p>
      </div>
    );
  }

  return (
    <div className="hero__content" dir={dir}>
      <span className="hero__badge">{model.badge}</span>

      <h1 className="hero__title">
        <TitleLines title={model.title} />
      </h1>

      <p className="hero__subtitle">{model.subtitle}</p>

      <div className="hero__actions">
        <HeroAction href={model.howItWorksButtonUrl} className="hero__btn-outline">
          {model.howItWorksButtonText}
        </HeroAction>
        <HeroAction href={model.reportButtonUrl} className="hero__btn-primary">
          {model.reportButtonText}
        </HeroAction>
      </div>
    </div>
  );
};

export const HeroBanner = () => {
  const { loading, error, model, dir } = useHero();

  if (loading && !model) {
    return (
      <div className="hero__banner" dir={dir}>
        <div className="hero__banner-media">
          <p style={{ color: "#64748b", fontSize: 14, padding: 24 }}>Loading banner…</p>
        </div>
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="hero__banner" dir={dir}>
        <div className="hero__banner-media">
          <p style={{ color: "#b91c1c", fontSize: 14, padding: 24 }}>
            {error || "Hero banner unavailable."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="hero__banner" dir={dir}>
      <div className="hero__banner-media">
        <img
          src={model.bannerUrl}
          alt={model.bannerAlt}
          className="hero__banner-img"
        />
        <div className="hero__banner-overlay" aria-hidden="true" />

        <div className="hero__stats">
          {model.stats.map((stat) => (
            <div key={stat.label} className="hero__stat-card">
              <div className="hero__stat-value">{stat.value}</div>
              <div className="hero__stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Hero = () => {
  return (
    <HeroProvider>
      <section className="hero">
        <HeroContent />
        <HeroBanner />
      </section>
    </HeroProvider>
  );
};

export default Hero;
